import { cn } from '@/lib/cn';
import { animate, AnimationPlaybackControls, motion } from 'framer-motion';
import useFitText from 'lib/useFitText';
import { useCallback, useEffect, useRef, useState } from 'react';
import usePrefersReducedMotion from '../../../lib/usePrefersReducedMotion';
import { DonutCenterValueProps } from './Config';

const ANIMATION_DURATION = 0.3 as const;

/**
 * Component that displays the center value of a donut chart with animations
 * and responsive text sizing.
 */
const DonutCenterValue: React.FC<DonutCenterValueProps> = ({
    value,
    formatValue,
    formatPercentage,
    showValuesAsPercentage,
    showPercentage,
    totalValue,
    className,
    style
}) => {
    const [isFinished, setIsFinished] = useState(false);
    const [formattedValue, setFormattedValue] = useState(() => formatValue(value));

    const animationRef = useRef<AnimationPlaybackControls>();
    const prevStateRef = useRef({ value, showPercentage });

    const prefersReducedMotion = usePrefersReducedMotion();

    const {
        fontSize,
        fontSizeValue,
        ref: counterRef
    } = useFitText({
        onFinish: () => setIsFinished(true)
    });

    const { fontSize: fontSizePercentage, fontSizeValue: fontSizePercentageValue, ref: percentageRef } = useFitText();

    const handleAnimationUpdate = useCallback(
        (currentValue: number) => {
            const valueFormatted = formatValue(currentValue);

            if (!showValuesAsPercentage) {
                setFormattedValue(valueFormatted);
                return;
            }

            const percentCompleted = 1 - Math.abs(value - currentValue) / Math.abs(value - prevStateRef.current.value);
            const shouldShowPercentage =
                showPercentage && (percentCompleted > 0.8 || prevStateRef.current.showPercentage);

            setFormattedValue(shouldShowPercentage ? formatPercentage(currentValue) : valueFormatted);
        },
        [formatValue, formatPercentage, showValuesAsPercentage, showPercentage, value]
    );

    useEffect(() => {
        if (animationRef.current?.isAnimating()) {
            animationRef.current.stop();
        }

        if (value === prevStateRef.current.value) {
            prevStateRef.current = { value, showPercentage };
            setFormattedValue(showPercentage ? formatPercentage(value) : formatValue(value));
            return;
        }

        animationRef.current = animate(prevStateRef.current.value, value, {
            duration: prefersReducedMotion ? 0 : ANIMATION_DURATION,
            onUpdate: handleAnimationUpdate,
            onComplete: () => {
                prevStateRef.current = { value, showPercentage };
            }
        });

        return () => animationRef.current?.stop();
    }, [value, formatValue, formatPercentage, prefersReducedMotion, handleAnimationUpdate, showPercentage]);

    const textScaling = showPercentage ? Math.min(fontSizePercentageValue / fontSizeValue, 1) : 1;

    return (
        <>
            {/* Hidden container for sizing calculations */}
            <div ref={counterRef} className={cn(className, 'invisible')} style={{ ...style, fontSize }}>
                <p className='font-semibold whitespace-nowrap'>
                    {formatValue(totalValue)} {'\u00A0'}
                </p>
            </div>

            {/* Hidden container for percentage sizing */}
            <div
                ref={percentageRef}
                className={cn(className, 'invisible')}
                style={{ ...style, fontSize: fontSizePercentage }}
            >
                {showValuesAsPercentage && <p className='font-semibold whitespace-nowrap'>{formatPercentage(value)}</p>}
            </div>

            {/* Visible animated value */}
            <motion.div
                className={cn(className, 'origin-center')}
                style={{
                    ...style,
                    fontSize,
                    opacity: isFinished ? 1 : 0
                }}
                transition={{ duration: ANIMATION_DURATION }}
                animate={{ scale: textScaling }}
            >
                <p className='font-semibold whitespace-nowrap'>{formattedValue}</p>
            </motion.div>
        </>
    );
};

export default DonutCenterValue;
